import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyIcon from "@mui/icons-material/VpnKey";
import { useAuth, API_BASE } from "./AuthContext";
import { Navigate } from "react-router-dom";

const ADMIN_EMAIL = "baltibronx@gmail.com";
const PASSWORD_MIN_LEN = 8;

interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  is_plus: number;
  stripe_customer_id: string | null;
  created_at: string;
  auth_provider: string | null;
  is_google: boolean;
  has_password: boolean;
}

export default function AdminPage({ prod }: { prod?: boolean }) {
  const { user, loading, token } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<number | null>(null);
  const [pwTarget, setPwTarget] = useState<AdminUser | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const authBase = prod ? "https://oriolebird.pythonanywhere.com" : API_BASE;

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    setError(null);
    try {
      const res = await fetch(`${authBase}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setUsers(await res.json());
    } catch (e: any) {
      setError("Failed to load users: " + e.message);
    } finally {
      setFetching(false);
    }
  }, [token, authBase]);

  useEffect(() => {
    if (loading) return;
    if (user?.email === ADMIN_EMAIL) {
      fetchUsers();
    } else {
      setFetching(false);
      setError(
        user
          ? `Signed in as ${user.email} — only ${ADMIN_EMAIL} can load this page.`
          : "Not signed in. Sign in with the admin account to load users."
      );
    }
  }, [loading, user, fetchUsers]);

  const handleTogglePlus = async (userId: number) => {
    if (!token) return;
    setToggling(userId);
    try {
      const res = await fetch(`${authBase}/admin/users/${userId}/toggle_plus`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updated = await res.json();
      setUsers((prev) =>
        prev.map((u) => (u.id === updated.id ? { ...u, is_plus: updated.is_plus } : u))
      );
    } catch (e: any) {
      setError("Toggle failed: " + e.message);
    } finally {
      setToggling(null);
    }
  };

  const openPasswordDialog = (u: AdminUser) => {
    setPwTarget(u);
    setPwValue("");
    setPwConfirm("");
    setPwError(null);
  };

  const handleSetPassword = async () => {
    if (!token || !pwTarget) return;
    if (pwValue.length < PASSWORD_MIN_LEN) {
      setPwError(`Password must be at least ${PASSWORD_MIN_LEN} characters.`);
      return;
    }
    if (pwValue !== pwConfirm) {
      setPwError("Passwords do not match.");
      return;
    }
    setPwSaving(true);
    setPwError(null);
    try {
      const res = await fetch(`${authBase}/admin/users/${pwTarget.id}/set_password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ new_password: pwValue }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `${res.status}`);
      setUsers((prev) =>
        prev.map((u) => (u.id === pwTarget.id ? { ...u, has_password: true } : u))
      );
      setNotice(`Password updated for ${pwTarget.email}.`);
      setPwTarget(null);
    } catch (e: any) {
      setPwError(e.message);
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return null;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  const filtered = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const plusCount = users.filter((u) => u.is_plus).length;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 4 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Admin — Users
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {users.length} total &nbsp;·&nbsp; {plusCount} PS Plus
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {fetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1.5px solid #c8d8e8", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f4f8fc" }}>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  PS Plus
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Stripe ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Password
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u) => (
                <TableRow
                  key={u.id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={u.picture ?? undefined}
                        alt={u.name ?? u.email}
                        sx={{ width: 28, height: 28, fontSize: 13 }}
                      >
                        {(u.name ?? u.email)[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">{u.name ?? "—"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(u.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={u.is_plus ? "Revoke Plus" : "Grant Plus"}>
                      <span>
                        <Switch
                          size="small"
                          checked={!!u.is_plus}
                          disabled={toggling === u.id}
                          onChange={() => handleTogglePlus(u.id)}
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {u.stripe_customer_id ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        u.is_google && !u.has_password
                          ? "Google sign-in — no password to set"
                          : "Set a new password"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={u.is_google && !u.has_password}
                          onClick={() => openPasswordDialog(u)}
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.disabled" }}>
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!pwTarget} onClose={() => !pwSaving && setPwTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Set password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: 14 }}>
            {pwTarget?.email}
          </DialogContentText>
          {pwError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pwError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="password"
            label="New password"
            autoComplete="new-password"
            value={pwValue}
            onChange={(e) => setPwValue(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label="Confirm password"
            autoComplete="new-password"
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwTarget(null)} disabled={pwSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSetPassword} disabled={pwSaving}>
            {pwSaving ? "Saving…" : "Set password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
